import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import { ProgramProvider } from './contexts/ProgramContext';

// Public Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import NotFoundPage from './pages/NotFoundPage';
import SubscriptionPage from './pages/SubscriptionPage';
import LGPDPage from './pages/LGPDPage';
import CookiesPage from './pages/CookiesPage';
import ProgramLandingPage from './pages/ProgramLandingPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage';
import BlogTaxonomyPage from './pages/BlogTaxonomyPage';

// Admin Pages
import LoginPage from './pages/admin/LoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import UsersListPage from './pages/admin/UsersListPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import UserEditPage from './pages/admin/UserEditPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import SubscriptionsPage from './pages/admin/SubscriptionsPage';
import CommunityPage from './pages/admin/CommunityPage';
import ChallengesPage from './pages/admin/ChallengesPage';
import ContentPage from './pages/admin/ContentPage';
import GamificationPage from './pages/admin/GamificationPage';
import MeditationManagement from './pages/admin/MeditationManagement';
import SettingsPage from './pages/admin/SettingsPage';
import NotificationsPage from './pages/admin/NotificationsPage';
import BrandingPage from './pages/admin/BrandingPage';
import ProgramsListPage from './pages/admin/ProgramsListPage';
import ProgramEditPage from './pages/admin/ProgramEditPage';
import BlogListPage from './pages/admin/BlogListPage';
import BlogEditPage from './pages/admin/BlogEditPage';
import SeoSettingsPage from './pages/admin/SeoSettingsPage';

// Styles
import './styles/global.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAdminAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/admin/login" />;
};

function App() {
  return (
    <HelmetProvider>
      <ProgramProvider>
        <AdminAuthProvider>
          <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/sobre" element={<AboutPage />} />
              <Route path="/privacidade" element={<PrivacyPage />} />
              <Route path="/termos" element={<TermsPage />} />
              <Route path="/contato" element={<ContactPage />} />
              <Route path="/assinar" element={<SubscriptionPage />} />
              <Route path="/lgpd" element={<LGPDPage />} />
              <Route path="/cookies" element={<CookiesPage />} />

              {/* Public Blog — declared BEFORE `/:programSlug` so /blog wins matching. */}
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/categoria/:slug" element={<BlogTaxonomyPage kind="category" />} />
              <Route path="/blog/tag/:slug" element={<BlogTaxonomyPage kind="tag" />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />

              {/* Program Landing Pages — `/freedom`, `/balance`, future slugs.
                  Resolved dynamically from src/data/programs.js. Falls back to
                  404 for unknown slugs. Placed AFTER all named public routes so
                  reserved paths (/sobre, /admin, etc.) win the match. */}
              <Route path="/:programSlug" element={<ProgramLandingPage />} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<LoginPage />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Navigate to="/admin/dashboard" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute>
                    <UsersListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/new"
                element={
                  <ProtectedRoute>
                    <UserEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/:id"
                element={
                  <ProtectedRoute>
                    <UserDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/:id/edit"
                element={
                  <ProtectedRoute>
                    <UserEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute>
                    <AnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/subscriptions"
                element={
                  <ProtectedRoute>
                    <SubscriptionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/community"
                element={
                  <ProtectedRoute>
                    <CommunityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/challenges"
                element={
                  <ProtectedRoute>
                    <ChallengesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/content"
                element={
                  <ProtectedRoute>
                    <ContentPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/gamification"
                element={
                  <ProtectedRoute>
                    <GamificationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/meditation"
                element={
                  <ProtectedRoute>
                    <MeditationManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/branding"
                element={
                  <ProtectedRoute>
                    <BrandingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/programs"
                element={
                  <ProtectedRoute>
                    <ProgramsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/programs/new"
                element={
                  <ProtectedRoute>
                    <ProgramEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/programs/:id/edit"
                element={
                  <ProtectedRoute>
                    <ProgramEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/blog"
                element={
                  <ProtectedRoute>
                    <BlogListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/blog/new"
                element={
                  <ProtectedRoute>
                    <BlogEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/blog/:id/edit"
                element={
                  <ProtectedRoute>
                    <BlogEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/seo"
                element={
                  <ProtectedRoute>
                    <SeoSettingsPage />
                  </ProtectedRoute>
                }
              />

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </div>
          </Router>
        </AdminAuthProvider>
      </ProgramProvider>
    </HelmetProvider>
  );
}

export default App;